    import {
    getAccessToken,
    setAccessToken,
    clearAccessToken,
    clearUser,
  } from 'api/tokenService'; 

  import { appNavigate } from 'lib/navigation';
  
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  let isRefreshing = false;

  type RefreshWaiter = {
    resolve: (token: string) => void;
    reject: (err: any) => void;
  };
  
  let refreshQueue: RefreshWaiter[] = [];
  
  async function refresh(): Promise<string> {
    if (isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        refreshQueue.push({ resolve, reject });
      });
    }
  
    isRefreshing = true;
  
    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!resp.ok) throw new Error('Refresh failed');
  
      const data: any = await resp.json().catch(() => ({}));
      const accessToken = data?.access_token as string | undefined;
      if (!accessToken) throw new Error('No access token in refresh');
  
      setAccessToken(accessToken);
  
      // wake up waiters with the new token
      refreshQueue.forEach(w => w.resolve(accessToken));
      return accessToken;
    } catch (err) {
      // propagate failure to waiters
      refreshQueue.forEach(w => w.reject(err));
      throw err;
    } finally {
      isRefreshing = false;
      refreshQueue = [];
    }
  }
   
  
  // helper: safely parse JSON or return null for 204/empty
  async function readJson(res: Response): Promise<any | null> {
    const text = await res.text();
    if (!text) return null;
    try { return JSON.parse(text); } catch { return null; }
  }
  
  type RequestOptions = {
    method?: string;
    body?: FormData | Record<string, unknown> | null;
    headers?: Record<string, string>;
    retry?: boolean;
  };

  async function request(path: string, { method = 'GET', body, headers = {}, retry = true }: RequestOptions = {}): Promise<any> {
    const url = API_BASE_URL + path;
    const token = getAccessToken();
  
    const baseHeaders: Record<string, string> = { ...headers };
    const isForm = (body instanceof FormData);
    if (!isForm) baseHeaders['Content-Type'] = baseHeaders['Content-Type'] || 'application/json';
    if (token) baseHeaders.Authorization = `Bearer ${token}`;
  
    const opts: RequestInit = {
      method,
      credentials: 'include',        // keep cookies flowing (refresh)
      headers: baseHeaders,
      ...(body ? { body: isForm ? body : JSON.stringify(body) } : {}),
    };
  
    let res = await fetch(url, opts);
  
    // If unauthorized, try one refresh attempt
    if (res.status === 401 && retry && token) {
      try {
        const newToken = await refresh();
        const retriedHeaders = { ...(opts.headers as Record<string, string>), Authorization: `Bearer ${newToken}` };
        res = await fetch(url, { ...opts, headers: retriedHeaders });
      } catch {
        clearAccessToken();
        clearUser();
        // Dispatch event to notify AuthContext that session has expired
        window.dispatchEvent(new CustomEvent('auth:session-expired'));
        appNavigate('/login', { replace: true });
        throw new Error('Authentication failed');
      }
    } else if (res.status === 403 && retry && path !== '/api/v1/auth/refresh') {
      // Do not logout on 403; surface to page for friendly message
      const payload = await readJson(res);
      const err = new Error((payload as any)?.detail || 'Forbidden');
      (err as any).status = 403; (err as any).data = payload;
      throw err;
    }
  
    if (!res.ok) {
      const payload = await readJson(res);
      let message = res.statusText;
      if (payload && Object.prototype.hasOwnProperty.call(payload, 'detail')) {
        const detail = (payload as any).detail;
        if (typeof detail === 'string') {
          message = detail;
        } else if (Array.isArray(detail)) {
          const msgs = (detail as any[]).map(e => e && (e as any).msg).filter(Boolean) as string[];
          if (msgs.length) message = msgs.join(', ');
        }
      }
      const error = new Error(message);
      (error as any).status = res.status;
      (error as any).data = payload;
      throw error;
    }
  
    return readJson(res);
  }
  
  // convenience
  export interface HttpClient {
    get<T = any>(path: string, options?: RequestOptions): Promise<T>;
    post<T = any>(path: string, body?: FormData | Record<string, unknown> | null, options?: RequestOptions): Promise<T>;
    put<T = any>(path: string, body?: FormData | Record<string, unknown> | null, options?: RequestOptions): Promise<T>;
    patch<T = any>(path: string, body?: FormData | Record<string, unknown> | null, options?: RequestOptions): Promise<T>;
    delete<T = any>(path: string, options?: RequestOptions): Promise<T>;
  }

  const http: HttpClient = {
    get:    (p: string, o?: RequestOptions)              => request(p, { ...o, method: 'GET' }),
    post:   (p: string, b?: FormData | Record<string, unknown> | null, o?: RequestOptions) => request(p, { ...o, method: 'POST',  body: b ?? null }),
    put:    (p: string, b?: FormData | Record<string, unknown> | null, o?: RequestOptions) => request(p, { ...o, method: 'PUT',   body: b ?? null }),
    patch:  (p: string, b?: FormData | Record<string, unknown> | null, o?: RequestOptions) => request(p, { ...o, method: 'PATCH', body: b ?? null }),
    delete: (p: string, o?: RequestOptions)              => request(p, { ...o, method: 'DELETE' }),
  };

  export default http;
  